import { useEffect, useState } from "react";
import { api } from "@/lib/nexus";
import { KeyRound, Loader2, CheckCircle2, Trash2, Cpu, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ provider: "openai_compat", base_url: "", model_id: "", api_key: "", active: true });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  const load = () =>
    api.get("/settings/llm").then((r) => {
      setCurrent(r.data);
      if (r.data.configured) {
        setForm((f) => ({
          ...f,
          provider: r.data.provider || "openai_compat",
          base_url: r.data.base_url || "",
          model_id: r.data.model_id || "",
          active: r.data.active,
        }));
      }
      setLoading(false);
    });

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus("");
    try {
      const payload = { ...form };
      if (!payload.api_key) delete payload.api_key; // keep existing key if left blank
      await api.put("/settings/llm", payload);
      setForm((f) => ({ ...f, api_key: "" }));
      setStatus("saved");
      await load();
    } catch (err) {
      setStatus(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const revert = async () => {
    if (!window.confirm("Revert to the built-in Emergent Claude key?")) return;
    await api.delete("/settings/llm");
    setForm({ provider: "openai_compat", base_url: "", model_id: "", api_key: "", active: true });
    setStatus("reverted");
    load();
  };

  return (
    <div className="p-4 space-y-4 max-w-3xl">
      <div>
        <div className="panel-title">PLATFORM SETTINGS // LLM PROVIDER</div>
        <h1 className="font-rajdhani font-bold text-2xl tracking-tight mt-1">
          Bring your own <span className="text-nexus-cyan">LLM key</span>
        </h1>
        <p className="text-[13px] text-nexus-text-2 mt-1">
          Configure a platform-wide LLM provider so this deployment does not depend on the Emergent key.
          Ideal for independent GCP deployments. The key is <span className="text-nexus-cyan">encrypted at rest</span> and powers
          NL graph queries, transaction/agent analysis, and agentic actions.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-nexus-text-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>
      ) : (
        <>
          <div className="panel brackets p-4" data-testid="active-llm-panel">
            <span className="b1" /><span className="b2" />
            <div className="panel-title mb-2"><Cpu size={12} className="inline mr-1" /> Active model</div>
            <div className="flex items-center gap-2">
              {current?.active ? <KeyRound size={14} className="text-nexus-amber" /> : <ShieldCheck size={14} className="text-nexus-violet" />}
              <span className="font-rajdhani font-semibold uppercase tracking-widest text-[14px]" data-testid="active-llm-label">
                {current?.label}
              </span>
              <span className={`text-[9px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${current?.active ? "border-nexus-amber/50 text-nexus-amber" : "border-nexus-violet/50 text-nexus-violet"}`}>
                {current?.active ? "custom key" : "emergent managed"}
              </span>
            </div>
            <div className="hash mt-1">{current?.model_id}</div>
            {current?.configured && current?.api_key && (
              <div className="text-[10px] font-mono text-nexus-text-3 mt-1">key {current.api_key}</div>
            )}
          </div>

          {!isAdmin && (
            <div data-testid="settings-admin-notice" className="panel p-3 border border-nexus-amber/40 bg-nexus-amber/5 text-[12px] font-mono text-nexus-amber flex items-center gap-2">
              <KeyRound size={13} /> Platform LLM configuration is admin-only. Sign in as an admin to change the provider.
            </div>
          )}

          <form onSubmit={save} className="panel brackets p-4 space-y-3" data-testid="llm-settings-form">
            <span className="b1" /><span className="b2" />
            <div className="panel-title"><KeyRound size={12} className="inline mr-1" /> Configure custom provider</div>

            <label className="block">
              <span className="panel-title">Provider</span>
              <select data-testid="settings-provider-select" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}
                className="mt-1 w-full bg-nexus-bg border border-nexus-border px-2 py-2 text-[12px] font-mono">
                <option value="openai_compat">OpenAI-Compatible (OpenAI · Together · Groq · OpenRouter · Anthropic OpenAI-compat · vLLM · Ollama)</option>
              </select>
            </label>

            <label className="block">
              <span className="panel-title">Base URL <span className="text-nexus-text-3">(blank = api.openai.com)</span></span>
              <input data-testid="settings-base-url-input" placeholder="https://api.openai.com/v1  ·  https://api.groq.com/openai/v1" value={form.base_url}
                onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                className="mt-1 w-full bg-nexus-bg border border-nexus-border px-2 py-2 text-[12px] font-mono focus:border-nexus-cyan/60 outline-none" />
            </label>

            <label className="block">
              <span className="panel-title">Model ID</span>
              <input data-testid="settings-model-id-input" required placeholder="gpt-4o-mini  ·  llama-3.3-70b-versatile  ·  claude-sonnet-4-5" value={form.model_id}
                onChange={(e) => setForm({ ...form, model_id: e.target.value })}
                className="mt-1 w-full bg-nexus-bg border border-nexus-border px-2 py-2 text-[12px] font-mono focus:border-nexus-cyan/60 outline-none" />
            </label>

            <label className="block">
              <span className="panel-title">API key {current?.configured && <span className="text-nexus-text-3">(leave blank to keep current)</span>}</span>
              <input data-testid="settings-api-key-input" type="password" placeholder="sk-…" value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                className="mt-1 w-full bg-nexus-bg border border-nexus-border px-2 py-2 text-[12px] font-mono focus:border-nexus-cyan/60 outline-none" />
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input data-testid="settings-active-toggle" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              <span className="text-[12px] font-mono">Use this key as the active platform LLM</span>
            </label>

            {status === "saved" && <div className="flex items-center gap-1.5 text-[11px] font-mono text-nexus-cyan" data-testid="settings-status"><CheckCircle2 size={12} /> Saved — custom LLM active.</div>}
            {status === "reverted" && <div className="flex items-center gap-1.5 text-[11px] font-mono text-nexus-violet" data-testid="settings-status"><CheckCircle2 size={12} /> Reverted to Emergent Claude.</div>}
            {status && status !== "saved" && status !== "reverted" && <div className="text-[11px] font-mono text-nexus-rose" data-testid="settings-status">{status}</div>}

            <div className="flex justify-between pt-1">
              <button type="button" data-testid="settings-clear-button" onClick={revert} disabled={!isAdmin}
                className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-2 border border-nexus-border hover:text-nexus-rose hover:border-nexus-rose/50 px-3 py-1.5 flex items-center gap-1 disabled:opacity-40">
                <Trash2 size={11} /> Revert to Emergent
              </button>
              <button type="submit" data-testid="settings-save-button" disabled={saving || !isAdmin}
                className="text-[10px] font-mono uppercase tracking-widest text-nexus-cyan border border-nexus-cyan/50 hover:bg-nexus-cyan/10 px-3 py-1.5 disabled:opacity-50 flex items-center gap-1">
                {saving && <Loader2 size={11} className="animate-spin" />} Save Provider
              </button>
            </div>
          </form>

          <div className="text-[10px] font-mono text-nexus-text-3">
            Signed in as <span className="text-nexus-text-2">{user?.email}</span> · {user?.auth_provider}
          </div>
        </>
      )}
    </div>
  );
}
